import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { DecipherMappingApi } from '../api/decipher-mapping.api';
import { DecipherMappingPayloadMapper } from '../api/mappers/decipher-mapping-payload.mapper';
import {
  BuyerQualification,
  LocaleQuestionMappings,
  QuestionMapping,
} from '../state/decipher/decipher-mapping-state.model';

export interface DecipherMappingLoadResult {
  localeMappings: LocaleQuestionMappings[];
  qualificationsByLocale: Record<string, BuyerQualification[]>;
}

@Injectable({ providedIn: 'root' })
export class DecipherMappingService {
  private readonly _api = inject(DecipherMappingApi);
  private readonly _payloadMapper = new DecipherMappingPayloadMapper();

  public loadAll(cmp: number): Observable<DecipherMappingLoadResult> {
    return this._api.mappings(cmp).pipe(
      switchMap((localeMappings) => {
        if (!localeMappings.length) {
          return of({ localeMappings, qualificationsByLocale: {} });
        }
        return forkJoin(
          localeMappings.map((localeMapping) =>
            this._api
              .qualifications(cmp, localeMapping.locale)
              .pipe(map((quals) => ({ locale: localeMapping.locale, quals }))),
          ),
        ).pipe(
          map((results) => ({
            localeMappings,
            qualificationsByLocale: results.reduce(
              (byLocale, result) => ({
                ...byLocale,
                [result.locale]: result.quals,
              }),
              {} as Record<string, BuyerQualification[]>,
            ),
          })),
        );
      }),
    );
  }

  public save(
    cmp: number,
    locale: string,
    questionMappings: QuestionMapping[],
  ): Observable<void> {
    const mappings = questionMappings.flatMap((questionMapping) =>
      this._payloadMapper.toPayload(questionMapping),
    );
    return this._api.saveMappings(cmp, locale, { locale, cmp, mappings });
  }
}
